<template>
  <Teleport to="#modal-container">
    <div v-if="upload && index !== null" class="fixed inset-0 h-screen w-screen bg-black/25 backdrop-blur-sm py-4 px-2 sm:px-6 sm:py-12 flex items-center justify-center z-80000 gap-3 sm:gap-4">
      <o-button
        icon="chevronLeft"
        :icon-size="20"
        size="lg"
        variant="dark"
        :disabled="index === 0"
        class="flex-shrink-0 shadow-lg"
        @click="changeActive(-1)"
      />
      <div
        class="h-full w-full max-h-3xl max-w-7xl flex flex-col-reverse sm:flex-row items-center bg-white rounded-md overflow-hidden shadow dark:bg-gray-950"
      >
        <div class="flex-grow w-full h-full flex items-center justify-center overflow-hidden">
          <!--<o-button icon="chevronLeft" transparent :disabled="index === 0" class="flex-shrink-0" @click="changeActive(-1)" />-->

          <template v-if="upload.imageUrls">
            <!--<img :src="upload.imageUrls.large" class="w-10 flex-grow sm:w-full flex-shrink sm:max-w-lg rounded-lg m-2">-->

            <img
              :src="upload.link || upload.imageUrls.large"
              :alt="upload.caption"
              class="h-full w-full object-contain bg-white dark:bg-gray-950"
            >
          </template>

          <template v-else-if="isPDF && upload.link">
            <iframe :src="upload.link" class="h-full w-full" />
          </template>

          <template v-else-if="isVideo && upload.link">
            <video controls autoplay class="h-full w-full bg-black">
              <source :src="upload.link" :type="upload.mimeType">
              {{ $t('components.upload.elements.preview.notSupported') }}
            </video>
          </template>

          <template v-else-if="upload">
            <div class="flex flex-col items-center justify-center">
              <div class="h-10 w-10 bg-blue-600 rounded-xl flex items-center justify-center font-semibold mb-2">
                <span class="text-white uppercase text-sm">{{ upload.extension }}</span>
              </div>
              <p class="text-gray-800 p-3 tet-center">
                {{ upload.caption }}
              </p>

              <o-button
                icon="download"
                variant="info"
                class="mt-4"
                @click="handleDownload"
              >
                {{ $t('components.upload.elements.preview.download') }}
              </o-button>
            </div>
          </template>

          <!--<o-button icon="chevronRight" transparent :disabled="index === data.length - 1" class="flex-shrink-0" @click="changeActive(1)" />-->
        </div>

        <div class="w-full sm:w-100 sm:h-full sm:border-l bg-white flex flex-col dark:bg-gray-900">
          <div class="flex items-center space-x-1 justify-between p-4 border-b">
            <div class="flex items-center space-x-1">
              <o-button size="sm" flat icon="delete" @click="handleDelete" />

              <o-button
                size="sm"
                flat
                icon="download"
                @click="handleDownload"
              />
            </div>

            <o-button size="sm" flat icon="x" @click="handleClose" />
          </div>

          <div class="p-4 border-b text-sm">
            <div v-if="upload.extension" class="grid grid-cols-3 gap-3">
              <p class="text-gray-500 dark:text-gray-400">
                {{ $t('file.fileType') }}
              </p>
              <p class="text-gray-700 uppercase col-span-2 mb-3 dark:text-gray-200">
                {{ upload.extension }}
              </p>
            </div>

            <div v-if="upload.size" class="grid grid-cols-3 gap-3">
              <p class="text-gray-500 dark:text-gray-400">
                {{ $t('file.fileSize') }}
              </p>
              <p class="text-gray-700 col-span-2 mb-3 dark:text-gray-200">
                {{ sizeLabel }}
              </p>
            </div>

            <div class="grid grid-cols-3 gap-3">
              <p class="text-gray-500 dark:text-gray-400">
                {{ $t('file.uploaded') }}
              </p>
              <p class="text-gray-700 col-span-2 mb-3 dark:text-gray-200">
                {{ upload.createdLabel }}
              </p>
            </div>

            <div v-if="upload.reservation" class="grid grid-cols-3 gap-3">
              <p class="text-gray-500 dark:text-gray-400">
                {{ $t('general.appointment') }}
              </p>
              <o-panel-link :path="`/appointment/${upload.reservation.idReservation}?date=${upload.reservation.date}`" class="text-blue-700 underline col-span-2 mb-3" @click="handleClose">
                {{ upload.reservation.infoLabel }}
              </o-panel-link>
            </div>

            <div v-if="upload.reservationCustomer" class="grid grid-cols-3 gap-3">
              <p class="text-gray-500 dark:text-gray-400">
                {{ $t('general.appointment') }}
              </p>
              <o-panel-link :path="`/appointment/attendee/${upload.reservationCustomer.idReservationCustomer}`" class="text-blue-700 underline col-span-2 mb-3" @click="handleClose">
                {{ upload.reservationCustomer.infoLabel }}
              </o-panel-link>
            </div>
          </div>

          <div class="flex-shrink h-full p-4 flex flex-col justify-between">
            <o-form ref="form">
              <o-field-group
                v-if="upload"
                v-slot="{ status }"
                :label="$t('general.caption')"
                :name="$t('components.upload.elements.preview.name')"
                rules="required"
                class="block mb-4"
              >
                <text-field v-model="_caption" :status="status" />
              </o-field-group>

              <o-field-group v-if="isDataTypePhoto && upload.isTypeCustomer" :label="$t('general.customerVisible')" :name="$t('general.customerVisible')" class="block mb-4">
                <toggle-field v-model="_customerVisible" :value="1" :uncheckedValue="0" />
              </o-field-group>

              <o-field-group v-if="isDataTypePhoto && upload.isTypeLocation" :label="$t('asset.uploadGalleryVisible')" :info="$t('asset.uploadGalleryVisibleInfo')" class="block mb-4">
                <toggle-field v-model="_allowInGallery" :value="true" :uncheckedValue="false" />
              </o-field-group>

              <o-field-group :label="$t('general.notes')" :name="$t('general.notes')" class="block mb-4">
                <text-area-field v-model="upload.notes" rows="6" />
              </o-field-group>
            </o-form>

            <div v-if="unsavedChanges">
              <o-button class="w-full" :loading="loading" @click="handleSave">
                {{ $t('general.save') }}
              </o-button>
            </div>
          </div>
        </div>
      </div>
      <o-button
        icon="chevronRight"
        :icon-size="20"
        size="lg"
        variant="dark"
        :disabled="index === data.length - 1"
        class="flex-shrink-0 shadow-lg"
        @click="changeActive(1)"
      />
    </div>
  </Teleport>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue'
import { useNuxtApp } from '#app'
import hash from 'object-hash'

import {
  TextField,
  ToggleField,
  TextAreaField,
} from '@/components/fields'

import { Upload } from '~/_api/models/upload'

const { $api, $i18n, $notification } = useNuxtApp()

interface Props {
  data?: any[]
}

const props = withDefaults(defineProps<Props>(), {
  data: () => [],
})

const emit = defineEmits<{
  (e: 'delete', upload: any): void
}>()

const form = ref<any>()
const index = ref<number | null>(null)
const upload = ref<any>(null)
const originalUpload = ref<any>(null)
const loading = ref(false)

const _caption = computed({
  get () {
    return upload.value?.caption || ''
  },
  set (value: string) {
    if (upload.value) {
      upload.value.caption = value
    }
  },
})

const _customerVisible = computed({
  get () {
    return upload.value?.customerVisible || ''
  },
  set (value: any) {
    if (upload.value) {
      upload.value.customerVisible = value
    }
  },
})

const _allowInGallery = computed({
  get () {
    return upload.value?.allowInGallery || false
  },
  set (value: any) {
    if (upload.value) {
      upload.value.allowInGallery = value
    }
  },
})

const sizeLabel = computed(() => {
  const u = upload.value

  return u.size > 1024
    ? u.size > 1048576
      ? Math.round(u.size / 1048576) + 'mb'
      : Math.round(u.size / 1024) + 'kb'
    : u.size + 'b'
})

const isDataTypePhoto = computed(() => {
  return upload.value.isDataTypePhoto
})

const isDataTypeDocument = computed(() => {
  return upload.value.isDataTypeDocument
})

const isPDF = computed(() => {
  return ['application/pdf'].includes(upload.value.mimeType)
})

const isVideo = computed(() => {
  return ['audio/webm', 'video/webm',
    'application/ogg', 'audio/oggm', 'video/ogg',
    'audio/mp4m', 'video/mp4'].includes(upload.value.mimeType)
})

const unsavedChanges = computed(() => {
  return (originalUpload.value && upload.value && originalUpload.value !== hash(upload.value.toJSON()))
})

watch(index, () => {
  if (index.value !== null && props.data[index.value]) {
    const item = props.data[index.value]

    upload.value = new Upload(getUploadData(item))
    originalUpload.value = hash(upload.value.toJSON())
  } else {
    upload.value = null
  }
})

function open (idx: number) {
  index.value = idx

  window.addEventListener('keydown', handleKeyDown)
}

function changeActive (value: number) {
  const idx = index.value! + value

  if (props.data[idx]) {
    index.value = idx
  }
}

function handleDelete () {
  emit('delete', upload.value)
}

function handleDownload () {
  const u = upload.value
  const link = u.link
  const name = u.caption
  const params = new URL(link, window.location.origin).searchParams

  const size = params.get('size')
  const modified = params.get('modified')
  const signature = params.get('signature')

  const baseURL = $api.upload.modelBaseUrl
  const url = baseURL + `/${u.objectID()}/serve?size=${size}&modified=${modified}&signature=${signature}`

  const el = document.createElement('a')

  el.href = url
  el.download = name
  el.click()
}

function getUploadData(item: any) {
  if (typeof item?.requestJSON === 'function') {
    return item.requestJSON()
  }

  if (typeof item?.toJSON === 'function') {
    return item.toJSON()
  }

  return item
}

function handleClose () {
  upload.value = null
  index.value = null

  window.removeEventListener('keydown', handleKeyDown)
}

function handleKeyDown ($event: KeyboardEvent) {
  if ($event.keyCode === 37) {
    changeActive(-1)
  }

  if ($event.keyCode === 39) {
    changeActive(1)
  }
}

async function handleSave () {
  loading.value = true

  try {
    const response = await $api.upload.save(upload.value)

    if (response.data) {
      $notification.toast({
        title: $i18n.t('components.upload.elements.preview.success'),
        text: $i18n.t('components.upload.elements.preview.successInfo'),
        variant: 'success',
      })
    }
  } catch (e: any) {
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

defineExpose({ open, index })
</script>
